import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Fuel, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Register() {
  const { register, formatApiError } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const navigate = useNavigate();

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const res = await register(name, email, password);
      // If backend returned a token, user was logged in. Otherwise show pending message.
      if (res && res.token) {
        toast.success("Account created");
        navigate("/");
      } else {
        toast.success("Registered — awaiting admin approval");
        navigate("/login");
      }
    } catch (e2) {
      const msg = formatApiError(e2);
      setErr(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A] p-6">
      <form
        onSubmit={onSubmit}
        className="w-full max-w-md ft-fade-up border border-[#262626] bg-[#121212] p-8"
        data-testid="register-form"
      >
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 bg-[#FF5A1F] flex items-center justify-center">
            <Fuel size={20} strokeWidth={2.5} className="text-black" />
          </div>
          <div>
            <p className="ft-overline">FUELTRACK</p>
            <h2 className="font-display text-2xl font-black tracking-tighter">Create account</h2>
          </div>
        </div>

        <label className="ft-overline block mb-2">Name</label>
        <input
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full bg-[#0A0A0A] border border-[#262626] px-4 py-3 font-mono text-white focus:outline-none focus:border-[#FF5A1F] focus:ring-1 focus:ring-[#FF5A1F] mb-4 transition-colors duration-200"
          data-testid="register-name-input"
        />

        <label className="ft-overline block mb-2">Email</label>
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full bg-[#0A0A0A] border border-[#262626] px-4 py-3 font-mono text-white focus:outline-none focus:border-[#FF5A1F] focus:ring-1 focus:ring-[#FF5A1F] mb-4 transition-colors duration-200"
          data-testid="register-email-input"
        />

        <label className="ft-overline block mb-2">Password</label>
        <input
          type="password"
          required
          minLength={6}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="w-full bg-[#0A0A0A] border border-[#262626] px-4 py-3 font-mono text-white focus:outline-none focus:border-[#FF5A1F] focus:ring-1 focus:ring-[#FF5A1F] mb-6 transition-colors duration-200"
          data-testid="register-password-input"
        />

        {err && (
          <div className="mb-4 text-[#EF4444] text-sm border border-[#EF4444]/30 bg-[#EF4444]/10 p-3">
            {err}
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          className="w-full bg-[#FF5A1F] text-black font-bold px-6 py-3 hover:bg-[#E04F1A] transition-colors duration-200 disabled:opacity-60 inline-flex items-center justify-center gap-2"
          data-testid="register-submit-button"
        >
          {loading && <Loader2 size={16} className="animate-spin" />}
          Create account
        </button>

        <p className="mt-6 text-[#A3A3A3] text-sm">
          Have an account?{" "}
          <Link to="/login" className="text-[#FF5A1F] hover:underline" data-testid="link-login">
            Sign in
          </Link>
        </p>
      </form>
    </div>
  );
}
